import React from 'react';
import { useQuery } from 'react-query';
import {
  FileText,
  Upload,
  Trash2,
  Clock,
  User,
  File,
  Image,
  FileCheck
} from 'lucide-react';
import api from '../services/api';

const FileUploadHistory = ({ permitId }) => {
  const { data, isLoading, error } = useQuery(
    ['fileHistory', permitId],
    async () => {
      const response = await api.get(`/permits/${permitId}/files/history`);
      return response.data;
    },
    {
      enabled: !!permitId
    }
  );

  const getFileIcon = (mimetype) => {
    if (!mimetype) return <File className="w-5 h-5 text-gray-500" />;

    if (mimetype.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-500" />;
    } else if (mimetype === 'application/pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else {
      return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'added':
        return <Upload className="w-5 h-5 text-green-500" />;
      case 'replaced':
        return <FileCheck className="w-5 h-5 text-yellow-500" />;
      case 'deleted':
        return <Trash2 className="w-5 h-5 text-red-500" />;
      default:
        return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'added':
        return 'bg-green-100 text-green-800';
      case 'replaced':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading file history: {error.message}</p>
      </div>
    );
  }

  const { fileHistory } = data || {};

  if (!fileHistory || fileHistory.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">No file upload history found</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {/* Header */}
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
        <FileText className="w-5 h-5" />
        File Upload History
      </h3>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* History items */}
        <div className="space-y-4">
          {fileHistory.map((entry, index) => {
            const metadata = entry.fileMetadata || {};

            return (
              <div key={entry.id} className="relative pl-14">
                {/* Icon */}
                <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
                  {getActionIcon(entry.action)}
                </div>

                {/* Content */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(entry.action)}`}>
                          {entry.action.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                          Version {entry.version}
                        </span>
                      </div>

                      {/* File info */}
                      <div className="flex items-center gap-2 mb-2">
                        {getFileIcon(metadata.mimetype)}
                        <p className="text-sm font-medium text-gray-900 truncate max-w-md">
                          {metadata.originalName || 'Unknown file'}
                        </p>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{formatFileSize(metadata.size)}</span>
                        <span className="capitalize">{metadata.mimetype?.split('/')[1] || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>

                  {/* User and timestamp */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="w-4 h-4" />
                      <span>{entry.uploader?.fullName || 'Unknown User'}</span>
                      {entry.uploader?.role && (
                        <span className="text-xs text-gray-500">
                          ({entry.uploader.role})
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      {formatDate(entry.created_at)}
                    </div>
                  </div>

                  {/* Comments */}
                  {entry.comments && (
                    <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs text-gray-600">{entry.comments}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Total {fileHistory.length} file operation{fileHistory.length !== 1 ? 's' : ''}
        </p>
      </div>
    </div>
  );
};

export default FileUploadHistory;
